// StudyDost — Login Page v2
// frontend/src/pages/LoginPage.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CLASSES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const BOARDS = ['CBSE', 'ICSE', 'BSEB', 'UP', 'OTHER'];

export default function LoginPage({ defaultRole = 'STUDENT', redirectTo = null }) {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState('phone'); // phone | otp | profile
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [studentClass, setStudentClass] = useState('');
  const [board, setBoard] = useState('CBSE');
  const [loading, setLoading] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);

  const handleSendOTP = async () => {
    if (phone.length !== 10) return toast.error('Enter a valid 10-digit number');
    setLoading(true);
    try {
      await authAPI.sendOTP(phone);
      toast.success('OTP sent on WhatsApp! 📱');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return toast.error('6-digit OTP enter karein');
    setLoading(true);
    try {
      const res = await authAPI.verifyOTP(phone, otp, undefined, undefined, defaultRole);
      const { token, user } = res.data;
      // If student with no class — show profile step, save token for later
      if (defaultRole === 'STUDENT' && !user.studentClass) {
        setPendingToken(token);
        setPendingUser(user);
        login(token, user); // log in immediately so PATCH /profile works
        setStep('profile');
      } else {
        login(token, user);
        navigate(redirectTo || getDefaultRedirect(user.roles));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Incorrect OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!studentClass) return toast.error('Please select your class');
    setLoading(true);
    try {
      // Use PATCH /profile — user is already logged in from verify step
      const { userAPI } = await import('../services/api');
      const res = await userAPI.updateProfile({ name, studentClass, board });
      // Update auth context with new data
      login(pendingToken, { ...pendingUser, ...res.data.user });
      toast.success(`Welcome, ${name || 'Student'}! 🎉`);
      navigate(redirectTo || '/chat');
    } catch (err) {
      // Non-critical — still navigate
      toast.error('Profile save mein problem, baad mein update karein');
      navigate(redirectTo || '/chat');
    } finally {
      setLoading(false);
    }
  };

  function getDefaultRedirect(roles = []) {
    // Normal login always goes to /chat regardless of roles.
    // Teacher/parent dashboards are accessed via their own portal URLs.
    // redirectTo prop handles /teacher/login and /parent/login cases explicitly.
    return '/chat';
  }

  const portalLabel =
    defaultRole === 'PARENT' ? '👨‍👩‍👦 Parent Portal'
    : defaultRole === 'TEACHER' ? '🎓 Teacher Portal'
    : 'Your Personal AI Tutor';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        {step !== 'phone' && (
          <button style={styles.backBtn} onClick={() => setStep(step === 'otp' ? 'phone' : 'otp')}>
            <ArrowLeft size={22} color="#1E40AF" />
          </button>
        )}
        <button style={styles.skipBtn} onClick={() => navigate('/')}>✕</button>
      </div>

      {/* Logo */}
      <div style={styles.logoArea}>
        <img src="/studydost_logo.png" alt="StudyDost" style={{ height: 64, width: "auto", marginBottom: 4 }} />
        <h1 style={styles.logoTitle}>StudyDost</h1>
        <p style={styles.logoSub}>{portalLabel}</p>
      </div>

      {/* Step: Phone */}
      {step === 'phone' && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Let's get you started.</h2>
          <p style={styles.cardDesc}>We'll send an OTP on WhatsApp. No password to remember.</p>
          <div style={styles.phoneInput}>
            <div style={styles.countryCode}>
              <span>🇮🇳</span>
              <span style={{ color: '#6B7280', fontSize: 15 }}>+91</span>
            </div>
            <input
              style={styles.phoneInputField}
              type="tel"
              placeholder="10-digit mobile number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              inputMode="numeric"
              autoFocus
            />
          </div>
          <button
            style={{ ...styles.btn, opacity: phone.length === 10 ? 1 : 0.5 }}
            onClick={handleSendOTP}
            disabled={loading || phone.length !== 10}
          >
            {loading ? <div style={styles.spinner} /> : <><span>Send OTP</span><ArrowRight size={18} /></>}
          </button>
          <div style={styles.secureNote}>
            <Shield size={14} color="#4F46E5" />
            <span><strong>Secure &amp; private.</strong> We don't sell your data. Parents and students get separate logins.</span>
          </div>
          <p style={styles.termsNote}>
            By continuing you agree to our{' '}
            <a href="/terms" style={styles.termsLink}>terms</a>
            {' '}&amp;{' '}
            <a href="/privacy" style={styles.termsLink}>privacy</a>.
          </p>
        </div>
      )}

      {/* Step: OTP */}
      {step === 'otp' && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Enter the OTP</h2>
          <p style={styles.cardDesc}>A 6-digit OTP has been sent to +91 {phone} on <strong>WhatsApp</strong> 📱</p>
          <input
            style={styles.otpInput}
            type="tel"
            placeholder="——————"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoFocus
            maxLength={6}
          />
          <button
            style={{ ...styles.btn, opacity: otp.length === 6 ? 1 : 0.5 }}
            onClick={handleVerifyOTP}
            disabled={loading || otp.length !== 6}
          >
            {loading ? <div style={styles.spinner} /> : <><span>Verify</span><ArrowRight size={18} /></>}
          </button>
          <button style={styles.resendBtn} onClick={handleSendOTP}>
            OTP nahi aaya? Dobara bhejo
          </button>
        </div>
      )}

      {/* Step: Profile (Student only) */}
      {step === 'profile' && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Complete Your Profile</h2>
          <p style={styles.cardDesc}>Yeh information se aapko better answers milenge</p>

          <input
            style={styles.nameInput}
            type="text"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <div style={{ marginBottom: 20 }}>
            <label style={styles.label}>Which class are you in? *</label>
            <div style={styles.classGrid}>
              {CLASSES.map((c) => (
                <button
                  key={c}
                  style={{ ...styles.classBtn, ...(studentClass === c ? styles.classBtnActive : {}) }}
                  onClick={() => setStudentClass(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={styles.label}>Your Board</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {BOARDS.map((b) => (
                <button
                  key={b}
                  style={{
                    padding: '8px 16px', borderRadius: 20, border: '2px solid',
                    borderColor: board === b ? '#1E40AF' : '#E5E7EB',
                    background: board === b ? '#1E40AF' : '#FFF',
                    color: board === b ? '#FFF' : '#374151',
                    fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  }}
                  onClick={() => setBoard(b)}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>

          <button
            style={{ ...styles.btn, opacity: studentClass ? 1 : 0.5 }}
            onClick={handleSaveProfile}
            disabled={loading || !studentClass}
          >
            {loading ? <div style={styles.spinner} /> : <span>Get Started! 🚀</span>}
          </button>
          <button style={styles.resendBtn} onClick={() => navigate('/chat')}>
            Skip for now
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: 'linear-gradient(160deg, #EFF6FF 0%, #FFFFFF 60%)', padding: '0 20px 40px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', minHeight: 52 },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4 },
  skipBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9CA3AF', marginLeft: 'auto' },
  logoArea: { textAlign: 'center', padding: '24px 0 32px' },
  logo: { fontSize: 56, marginBottom: 8 },
  logoTitle: { fontSize: 34, fontWeight: 800, color: '#1E40AF', marginBottom: 4 },
  logoSub: { fontSize: 15, color: '#6B7280', fontStyle: 'italic' },
  card: { background: '#FFFFFF', borderRadius: 20, padding: '28px 24px', boxShadow: '0 4px 20px rgba(30,64,175,0.1)' },
  cardTitle: { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 8 },
  cardDesc: { fontSize: 14, color: '#6B7280', marginBottom: 24, lineHeight: 1.5 },
  phoneInput: { display: 'flex', alignItems: 'center', border: '2px solid #DBEAFE', borderRadius: 12, overflow: 'hidden', marginBottom: 20 },
  countryCode: { display: 'flex', alignItems: 'center', gap: 6, padding: '14px 12px', background: '#F3F4F6', borderRight: '2px solid #DBEAFE', whiteSpace: 'nowrap' },
  phoneInputField: { flex: 1, border: 'none', padding: '14px 16px', fontSize: 18, outline: 'none', fontWeight: 600 },
  otpInput: { width: '100%', border: '2px solid #DBEAFE', borderRadius: 12, padding: '14px 16px', fontSize: 28, fontWeight: 700, letterSpacing: 8, textAlign: 'center', outline: 'none', marginBottom: 20 },
  nameInput: { width: '100%', border: '2px solid #E5E7EB', borderRadius: 12, padding: '14px 16px', fontSize: 16, outline: 'none', marginBottom: 20 },
  btn: { width: '100%', padding: '15px', background: '#1E40AF', color: '#FFFFFF', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 },
  spinner: { width: 20, height: 20, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#FFFFFF', borderRadius: '50%', animation: 'spin 0.7s linear infinite' },
  secureNote: { display: 'flex', alignItems: 'flex-start', gap: 8, background: '#EEF2FF', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#374151', lineHeight: 1.5 },
  termsNote: { textAlign: 'center', fontSize: 11, color: '#9CA3AF', marginTop: 8 },
  termsLink: { color: '#4F46E5', fontWeight: 600 },
  resendBtn: { width: '100%', background: 'none', border: 'none', color: '#1E40AF', fontSize: 14, cursor: 'pointer', textDecoration: 'underline', padding: '8px 0' },
  label: { display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 },
  classGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 },
  classBtn: { padding: '10px 4px', border: '2px solid #E5E7EB', borderRadius: 10, background: '#FFFFFF', fontSize: 15, fontWeight: 600, cursor: 'pointer', color: '#374151' },
  classBtnActive: { background: '#1E40AF', borderColor: '#1E40AF', color: '#FFFFFF' },
};
